import { createFileRoute, getRouteApi, notFound, redirect, useLoaderData } from '@tanstack/react-router'
import { z } from 'zod'
import { ProtectionNotFound } from '../../components/errors/NotFound.tsx'
import { measurementAlertQueryOptions } from '../../data/measurementAlerts.ts'
import { lightningProtectionQueryOptions, lightningProtectionsQuery } from '../../data/protections/lightning.ts'
import { protectionQuery, protectionsQuery } from '../../data/protections/protections.ts'
import { hasRequiredRole } from '../../data/userProfile.ts'
import { ProtectionForm } from '../../features/alerts/ProtectionForm.tsx'

export const Route = createFileRoute('/_app/alerts_/$vrn/$protectionType/edit')({
	validateSearch: z.object({
		locationId: z.string().optional(),
	}),
	component: RouteComponent,
	notFoundComponent: () => <ProtectionNotFound />,
	beforeLoad: async ({ context: { queryClient, impersonation, isAdmin, userRoleName, sub }, params }) => {
		const { vrn, protectionType } = params
		const isProtect =
			protectionType === 'hail' ||
			protectionType === 'lightning' ||
			protectionType === 'weatherHazard' ||
			protectionType === 'wind' ||
			protectionType === 'precipitation' ||
			protectionType === 'webRelay'
		const isMeasurement = protectionType === 'measurement'
		const isSuperAdminRequired = protectionType === 'webRelay'
		const hasProtectionsSub = sub.features.includes('protections')
		if (
			!isAdmin ||
			(isSuperAdminRequired && !hasRequiredRole(userRoleName, 'SUPER_ADMIN')) ||
			(!impersonation && !hasProtectionsSub && isProtect) ||
			(!impersonation && !sub.features.includes('devices') && isMeasurement)
		) {
			throw redirect({ to: '/' })
		}

		await Promise.all([
			queryClient.ensureQueryData(protectionsQuery(impersonation, { enabled: hasProtectionsSub })),
			queryClient.ensureQueryData(lightningProtectionsQuery(impersonation, { enabled: hasProtectionsSub })),
		])

		if (
			protectionType === 'hail' ||
			protectionType === 'wind' ||
			protectionType === 'weatherHazard' ||
			protectionType === 'precipitation'
		) {
			const protection = await queryClient.ensureQueryData(protectionQuery(vrn, impersonation))
			return { protection: protection }
		}
		if (protectionType === 'lightning') {
			const areaIds = JSON.parse(vrn) // Vrn is areaIds array for lightning protection
			const lightningProtection = await queryClient.ensureQueryData(lightningProtectionQueryOptions(areaIds, impersonation))
			return { protection: lightningProtection }
		}
		if (protectionType === 'measurement') {
			const partyAccountId = impersonation?.partyAccountId
			const measurementAlerts = await queryClient.ensureQueryData(measurementAlertQueryOptions(partyAccountId))
			return { protection: measurementAlerts.find((alert) => alert.id === vrn) || null }
		}
		if (protectionType === 'webRelay') {
			return { protection: { type: 'webRelay' } as const }
		}

		return { protection: null }
	},
	loader: ({ context: { protection } }) => {
		if (!protection) {
			throw notFound()
		}
		return protection
	},
})

const route = getRouteApi('/_app/alerts_/$vrn/$protectionType/edit')

function RouteComponent() {
	const protection = useLoaderData({ from: '/_app/alerts_/$vrn/$protectionType/edit' })
	const { locationId } = route.useSearch({
		select: ({ locationId }) => ({ locationId }),
	})
	return <ProtectionForm locationId={locationId} editProtection={protection} />
}
